import { useEffect, useRef, useCallback, useState } from "react";
import { useAtom, useAtomValue } from "@effect-atom/atom-react";
import { MonacoEditor } from "./MonacoEditor";
import { TabBar } from "./TabBar";
import {
  resultContentAtom,
  activeResultTabAtom,
  showHtmlTabAtom,
  showPdfTabAtom,
  showConsoleAtom,
  pdfContentAtom,
  consoleMessagesAtom,
  generatePdfAtom,
} from "../atoms";
import type { ResultTab } from "../lib/schemas";

type MessageType = "info" | "warning" | "plain";

function parseMessage(message: string): { type: MessageType; text: string } {
  if (message.startsWith("[INFO] ")) {
    return { type: "info", text: message.slice(7) };
  }
  if (message.startsWith("[WARNING] ")) {
    return { type: "warning", text: message.slice(10) };
  }
  return { type: "plain", text: message };
}

export function ResultPanel() {
  const [activeTab, setActiveTab] = useAtom(activeResultTabAtom);
  const resultContent = useAtomValue(resultContentAtom);
  const showHtmlTab = useAtomValue(showHtmlTabAtom);
  const showPdfTab = useAtomValue(showPdfTabAtom);
  const showConsole = useAtomValue(showConsoleAtom);
  const pdfContent = useAtomValue(pdfContentAtom);
  const consoleMessages = useAtomValue(consoleMessagesAtom);
  const htmlIframeRef = useRef<HTMLIFrameElement>(null);
  const pdfIframeRef = useRef<HTMLIFrameElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [, triggerGeneratePdf] = useAtom(generatePdfAtom);

  const tabs: { id: ResultTab; label: string; hidden?: boolean }[] = [
    { id: "result", label: "Result" },
    { id: "html", label: "HTML Preview", hidden: !showHtmlTab },
    { id: "pdf", label: "PDF Preview", hidden: !showPdfTab },
    { id: "console", label: `Console${consoleMessages.length > 0 ? ` (${consoleMessages.length})` : ""}`, hidden: !showConsole },
  ];

  // Generate PDF using atom
  const generatePdf = useCallback(() => {
    if (!resultContent.includes("http://www.w3.org/1999/XSL/Format")) {
      return;
    }

    setIsGeneratingPdf(true);
    triggerGeneratePdf(undefined);
  }, [resultContent, triggerGeneratePdf]);

  // Reset loading state when PDF content is available
  useEffect(() => {
    if (pdfContent) {
      setIsGeneratingPdf(false);
    }
  }, [pdfContent]);

  // Auto-generate PDF when on PDF tab and result changes
  useEffect(() => {
    if (activeTab === "pdf" && showPdfTab && !pdfContent && !isGeneratingPdf) {
      generatePdf();
    }
  }, [activeTab, showPdfTab, pdfContent, isGeneratingPdf, generatePdf]);

  // Handle tab change with PDF generation
  const handleTabChange = useCallback(
    (tab: ResultTab) => {
      setActiveTab(tab);
      if (tab === "pdf" && showPdfTab && !pdfContent && !isGeneratingPdf) {
        generatePdf();
      }
    },
    [setActiveTab, showPdfTab, pdfContent, isGeneratingPdf, generatePdf]
  );

  // Update HTML preview when result changes and tab is active
  useEffect(() => {
    if (activeTab === "html" && htmlIframeRef.current && showHtmlTab) {
      const doc = htmlIframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(resultContent);
        doc.close();
      }
    }
  }, [activeTab, resultContent, showHtmlTab]);

  // Update PDF viewer when content is available
  useEffect(() => {
    if (pdfIframeRef.current && pdfContent) {
      pdfIframeRef.current.src = `data:application/pdf;base64,${pdfContent}`;
    }
  }, [pdfContent]);

  return (
    <div className="right-panel">
      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        className="result-tabs"
      />
      <div className="editor-container">
        <div
          style={{
            height: "100%",
            display: activeTab === "result" ? "block" : "none",
          }}
        >
          <MonacoEditor
            value={resultContent}
            language="xml"
            readOnly
          />
        </div>
        <div
          id="htmlContainer"
          style={{
            height: "100%",
            display: activeTab === "html" ? "block" : "none",
          }}
        >
          <iframe
            ref={htmlIframeRef}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              background: "white",
            }}
            title="HTML Preview"
          />
        </div>
        <div
          id="pdfContainer"
          style={{
            height: "100%",
            display: activeTab === "pdf" ? "block" : "none",
          }}
        >
          {!pdfContent ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#666",
              }}
            >
              {isGeneratingPdf ? "Generating PDF..." : "Click to generate PDF"}
            </div>
          ) : (
            <iframe
              ref={pdfIframeRef}
              style={{ width: "100%", height: "100%", border: "none" }}
              title="PDF Preview"
            />
          )}
        </div>
        <div
          id="consoleContainer"
          className="console-container"
          style={{
            height: "calc(100% - 16px)",
            display: activeTab === "console" ? "flex" : "none",
            flexDirection: "column",
          }}
        >
          {consoleMessages.length === 0 ? (
            <div className="console-empty">
              No messages. Use xsl:message in your stylesheet to output messages here.
            </div>
          ) : (
            <div className="console-messages">
              {consoleMessages.map((message, index) => {
                const { type, text } = parseMessage(message);
                return (
                  <div key={index} className={`console-message console-message-${type}`}>
                    <span className="console-message-type">{type.toUpperCase()}</span>
                    <span className="console-message-text">{text}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
